/* Notifications toggle for the /dash page.
   - Handles service worker registration
   - Injects the PWA manifest link (only on /dash)
   - Subscribes / unsubscribes via /api/dash/push
*/
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Bell, BellOff, Loader2, Check, X, Info, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dash/push`;
const MANIFEST_HREF = "/dash-manifest.webmanifest";
const SW_URL = "/sw.js";

// Convert URL-safe base64 → Uint8Array (needed for applicationServerKey)
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function useManifestLink() {
  useEffect(() => {
    // Inject <link rel="manifest"> only while /dash is mounted
    const existing = document.querySelector('link[rel="manifest"]');
    let created = null;
    if (!existing) {
      created = document.createElement("link");
      created.rel = "manifest";
      created.href = MANIFEST_HREF;
      document.head.appendChild(created);
    }
    // theme-color meta for iOS status-bar tint when installed
    let theme = document.querySelector('meta[name="theme-color"]');
    if (!theme) {
      theme = document.createElement("meta");
      theme.name = "theme-color";
      theme.content = "#0f172a";
      document.head.appendChild(theme);
    }
    return () => {
      if (created) document.head.removeChild(created);
    };
  }, []);
}

export default function NotificationsToggle({ pin }) {
  useManifestLink();

  // Environment capability flags
  const supportsSW = typeof navigator !== "undefined" && "serviceWorker" in navigator;
  const supportsPush =
    typeof window !== "undefined" && "PushManager" in window;
  const isIOS =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  // Check current subscription state on mount
  useEffect(() => {
    if (!supportsSW || !supportsPush) return;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch (e) {
        // Silent — subscription check is best-effort
      }
    })();
  }, [supportsSW, supportsPush]);

  const subscribe = useCallback(async () => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      // 1. Register (or re-use) service worker
      const reg =
        (await navigator.serviceWorker.getRegistration()) ||
        (await navigator.serviceWorker.register(SW_URL));

      // 2. Ask for permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setError("Permission refusée par le navigateur.");
        return;
      }

      // 3. Fetch VAPID public key
      const { data: keyResp } = await axios.get(`${API}/vapid-public-key`);
      if (!keyResp?.key) throw new Error("VAPID key indisponible");

      // 4. Subscribe via the browser
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyResp.key),
      });

      // 5. Send subscription to backend (PIN-authenticated)
      await axios.post(`${API}/subscribe`, {
        pin,
        subscription: subscription.toJSON(),
        device_label: navigator.userAgent.slice(0, 80),
      });

      setSubscribed(true);
      setInfo("Notifications activées sur cet appareil.");
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "Échec de l'activation des notifications."
      );
    } finally {
      setLoading(false);
    }
  }, [pin]);

  const unsubscribe = useCallback(async () => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await axios.post(`${API}/unsubscribe`, {
          pin,
          endpoint: sub.endpoint,
        });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      setInfo("Notifications désactivées sur cet appareil.");
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "Échec de la désactivation."
      );
    } finally {
      setLoading(false);
    }
  }, [pin]);

  const sendTest = useCallback(async () => {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/test`, { pin });
      const sent = res.data?.sent ?? 0;
      setInfo(`Test envoyé (${sent} appareil${sent > 1 ? "s" : ""}).`);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Test failed.");
    } finally {
      setLoading(false);
    }
  }, [pin]);

  // iOS + not standalone → user MUST "Add to Home Screen" first
  const iosNeedsInstall = isIOS && !isStandalone;

  if (!supportsSW || !supportsPush) {
    return (
      <div
        className="mt-4 p-3 rounded-xl border border-slate-700 bg-slate-900/60 text-slate-400 text-xs"
        data-testid="notif-unsupported"
      >
        <Info className="w-4 h-4 inline mr-1.5 -mt-0.5 text-slate-500" />
        Ce navigateur ne supporte pas les notifications Web Push.
      </div>
    );
  }

  return (
    <div
      className="mt-4 p-4 rounded-xl border border-slate-700 bg-slate-900/60"
      data-testid="notif-panel"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center">
          {subscribed ? (
            <Bell className="w-4 h-4 text-amber-300" />
          ) : (
            <BellOff className="w-4 h-4 text-slate-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm">
            Notifications push
          </div>
          <div className="text-slate-400 text-xs">
            {subscribed
              ? "Actif sur cet appareil — tu reçois une notif pour chaque nouveau lead / clic tél."
              : "Recevoir une notif iPhone à chaque nouveau lead."}
          </div>
        </div>
      </div>

      {iosNeedsInstall && !subscribed && (
        <div
          className="mt-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs leading-relaxed"
          data-testid="ios-install-hint"
        >
          <strong>Étape iOS requise (une fois) :</strong>
          <ol className="list-decimal pl-4 mt-1 space-y-0.5">
            <li>Bouton {"«"} Partager {"»"} en bas de Safari</li>
            <li>{"«"} Sur l&apos;écran d&apos;accueil {"»"}</li>
            <li>Ouvre ensuite l&apos;icône depuis l&apos;écran d&apos;accueil</li>
            <li>Reviens ici et clique {"«"} Activer les notifications {"»"}</li>
          </ol>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {!subscribed ? (
          <Button
            data-testid="notif-enable-button"
            disabled={loading}
            onClick={subscribe}
            className="bg-amber-600 hover:bg-amber-700 text-white font-semibold"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            Activer les notifications
          </Button>
        ) : (
          <>
            <Button
              data-testid="notif-test-button"
              disabled={loading}
              onClick={sendTest}
              variant="outline"
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Envoyer un test
            </Button>
            <Button
              data-testid="notif-disable-button"
              disabled={loading}
              onClick={unsubscribe}
              variant="outline"
              className="border-red-800/60 text-red-300 hover:bg-red-950/40"
            >
              <BellOff className="w-4 h-4 mr-2" />
              Désactiver
            </Button>
          </>
        )}
      </div>

      {error && (
        <div className="mt-2 flex items-start gap-2 text-red-400 text-xs" data-testid="notif-error">
          <X className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {info && (
        <div className="mt-2 flex items-start gap-2 text-emerald-400 text-xs" data-testid="notif-info">
          <Check className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{info}</span>
        </div>
      )}
      {permission === "denied" && !subscribed && (
        <div className="mt-2 text-amber-400 text-xs">
          Permission bloquée dans le navigateur — active-la manuellement dans les paramètres du site.
        </div>
      )}
    </div>
  );
}
